// src/services/tableService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class TableService {
  
  // 🪑 Get all tables with pagination support
  async getAllTables(params = {}) {
    try {
      console.log('🪑 Fetching all tables with params:', params)
      
      const response = await ApiService.get(AppURLs.TABLES.GET_ALL, params)
      console.log('✅ Tables fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching tables:', error)
      throw error
    }
  }

  // 🪑 Get table by ID
  async getTableById(id) {
    try {
      console.log('🪑 Fetching table by ID:', id)
      
      if (!id) {
        throw new Error('Table ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      
      console.log('✅ Table fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching table:', error)
      throw error
    }
  }

  // 🪑 Create new table
  async createTable(tableData) {
    try {
      console.log('🪑 Creating new table with data:', tableData)
      
      // Validate required fields
      this.validateTableData(tableData)
      
      // Format data for API (don't include availability for creation - let backend default to 'available')
      const formattedData = {
        number: tableData.number?.trim(),
        branch: tableData.branch,
        capacity: parseInt(tableData.capacity),
        isActive: tableData.isActive !== undefined ? tableData.isActive : true
      }
      
      console.log('🪑 Formatted table data for API:', formattedData)
      
      const response = await ApiService.post(AppURLs.TABLES.CREATE, formattedData)
      
      console.log('✅ Table created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating table:', error)
      throw error
    }
  }

  // 🪑 Update table using PATCH
  async updateTable(id, tableData) {
    try {
      console.log('🪑 Updating table:', { id, tableData })
      
      if (!id) {
        throw new Error('Table ID is required')
      }
      
      // Validate partial data for updates
      this.validatePartialTableData(tableData)
      
      // Format data for API - only include fields that are provided
      const formattedData = {}
      
      if (tableData.number !== undefined) {
        formattedData.number = tableData.number?.trim()
      }
      
      if (tableData.branch !== undefined) {
        formattedData.branch = tableData.branch
      }
      
      if (tableData.capacity !== undefined) {
        formattedData.capacity = typeof tableData.capacity === 'number' 
          ? tableData.capacity 
          : parseInt(tableData.capacity)
      }
      
      if (tableData.availability !== undefined) {
        formattedData.availability = tableData.availability
      }
      
      if (tableData.isActive !== undefined) {
        formattedData.isActive = tableData.isActive
      }
      
      console.log('🪑 Formatted update data for API:', formattedData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, formattedData)
      
      console.log('✅ Table updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating table:', error)
      throw error
    }
  }

  // 🪑 Update table availability status only
  async updateTableAvailability(id, availability) {
    try {
      console.log('🪑 Updating table availability:', { id, availability })
      
      if (!id) {
        throw new Error('Table ID is required')
      }
      
      if (!availability) {
        throw new Error('Availability status is required')
      }
      
      // Only send the availability field to minimize validation issues
      const updateData = {
        availability: availability
      }
      
      console.log('🪑 Availability update data:', updateData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, updateData)
      
      console.log('✅ Table availability updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating table availability:', error)
      throw error
    }
  }

  // 🪑 Delete table (sets status to inactive)
  async deleteTable(id) {
    try {
      console.log('🪑 Deleting table with ID:', id)
      
      if (!id) {
        throw new Error('Table ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      
      console.log('✅ Table deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting table:', error)
      throw error
    }
  }

  // 🪑 Get tables by branch
  async getTablesByBranch(branchId, params = {}) {
    try {
      console.log('🪑 Fetching tables by branch:', branchId)
      
      if (!branchId) {
        throw new Error('Branch ID is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.BY_BRANCH, { branchId })
      const response = await ApiService.get(endpoint, params)
      
      console.log('✅ Tables by branch fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching tables by branch:', error)
      throw error
    }
  }

  // 🪑 Get tables by availability
  async getTablesByAvailability(availability, params = {}) {
    try {
      console.log('🪑 Fetching tables by availability:', availability)
      
      if (!availability) {
        throw new Error('Availability status is required')
      }

      const endpoint = ApiService.replaceUrlParams(AppURLs.TABLES.BY_AVAILABILITY, { availability })
      const response = await ApiService.get(endpoint, params)
      
      console.log('✅ Tables by availability fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching tables by availability:', error)
      throw error
    }
  }

  // 🪑 Search tables
  async searchTables(searchTerm, params = {}) {
    try {
      console.log('🪑 Searching tables with term:', searchTerm)
      
      const searchParams = {
        ...params,
        search: searchTerm
      }
      
      const response = await ApiService.get(AppURLs.TABLES.SEARCH, searchParams)
      
      console.log('✅ Table search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching tables:', error)
      throw error
    }
  }

  // 🪑 Validate table number uniqueness
  async validateTableNumber(number, branchId, excludeId = null) {
    try {
      console.log('🪑 Validating table number:', { number, branchId, excludeId })
      
      const params = {
        number,
        branch: branchId
      }
      
      if (excludeId) {
        params.excludeId = excludeId
      }
      
      const response = await ApiService.get(AppURLs.TABLES.VALIDATE_NUMBER, params)
      
      console.log('✅ Table number validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating table number:', error)
      throw error
    }
  }

  // 🪑 Get table statistics
  async getTableStats() {
    try {
      console.log('🪑 Fetching table statistics')
      
      const response = await ApiService.get(AppURLs.TABLES.STATS)
      
      console.log('✅ Table statistics fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching table statistics:', error)
      throw error
    }
  }

  // 🪑 Bulk delete tables
  async bulkDeleteTables(tableIds) {
    try {
      console.log('🪑 Bulk deleting tables:', tableIds)
      
      if (!Array.isArray(tableIds) || tableIds.length === 0) {
        throw new Error('Table IDs array is required')
      }

      const response = await ApiService.post(AppURLs.TABLES.BULK_DELETE, { ids: tableIds })
      
      console.log('✅ Tables bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting tables:', error)
      throw error
    }
  }

  // 🪑 Bulk update table status
  async bulkUpdateStatus(tableIds, isActive) {
    try {
      console.log('🪑 Bulk updating table status:', { tableIds, isActive })
      
      if (!Array.isArray(tableIds) || tableIds.length === 0) {
        throw new Error('Table IDs array is required')
      }

      const response = await ApiService.post(AppURLs.TABLES.BULK_STATUS, { 
        ids: tableIds,
        isActive 
      })
      
      console.log('✅ Table status bulk updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating table status:', error)
      throw error
    }
  }

  // 🛡️ Validation Methods
  
  // Validate table data for creation
  validateTableData(data) {
    const errors = []

    // Required field validations
    if (!data.number || !data.number.trim()) {
      errors.push('Table number is required')
    }

    if (!data.branch || !data.branch.trim()) {
      errors.push('Branch selection is required')
    }

    if (data.capacity === undefined || data.capacity === null) {
      errors.push('Table capacity is required')
    } else if (isNaN(data.capacity) || parseInt(data.capacity) < 1) {
      errors.push('Capacity must be a positive number')
    } else if (parseInt(data.capacity) > 50) {
      errors.push('Capacity cannot exceed 50')
    }

    // String length validations
    if (data.number && data.number.length > 20) {
      errors.push('Table number must not exceed 20 characters')
    }

    // Pattern validations
    if (data.number && !/^[A-Za-z0-9\-_]+$/.test(data.number)) {
      errors.push('Table number can only contain letters, numbers, hyphens, and underscores')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // Validate partial table data for updates
  validatePartialTableData(data) {
    const errors = []

    // Optional fields validation (only validate if provided)
    if (data.number !== undefined) {
      if (typeof data.number !== 'string' || data.number.trim().length === 0) {
        errors.push('Table number must be a non-empty string')
      }
      if (data.number.length > 20) {
        errors.push('Table number must not exceed 20 characters')
      }
      if (!/^[A-Za-z0-9\-_]+$/.test(data.number)) {
        errors.push('Table number can only contain letters, numbers, hyphens, and underscores')
      }
    }

    if (data.branch !== undefined) {
      if (typeof data.branch !== 'string' || data.branch.trim().length === 0) {
        errors.push('Branch ID must be a non-empty string')
      }
    }

    if (data.capacity !== undefined) {
      if (isNaN(data.capacity) || parseInt(data.capacity) < 1) {
        errors.push('Capacity must be a positive number')
      }
      if (parseInt(data.capacity) > 50) {
        errors.push('Capacity cannot exceed 50')
      }
    }

    if (data.availability !== undefined) {
      const validAvailability = ['available', 'reserved']
      if (!validAvailability.includes(data.availability)) {
        errors.push('Availability must be either "available" or "reserved"')
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // Helper method to format table response
  formatTableResponse(table) {
    return {
      _id: table._id,
      number: table.number,
      branch: table.branch,
      branchDetails: table.branchDetails || table.branch,
      capacity: table.capacity,
      availability: table.availability || 'available',
      isActive: table.isActive,
      createdAt: table.createdAt,
      updatedAt: table.updatedAt,
      createdBy: table.createdBy
    }
  }

  // Helper method to format tables list response
  formatTablesResponse(response) {
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch tables')
    }

    const tables = response.data?.tables || response.data?.docs || response.data || []
    
    return {
      ...response,
      data: {
        ...response.data,
        tables: Array.isArray(tables) ? tables.map(this.formatTableResponse) : []
      }
    }
  }
}

export default new TableService()